import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Info, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Course } from "@/types/courseTypes";
import { useFirebase } from "@/contexts/FirebaseContext";
import { getCourseById } from "@/utils/firestoreService";

const IndividualCourseView = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useFirebase();

  useEffect(() => {
    const loadCourseData = async () => {
      if (!courseId) return;
      
      setLoading(true);
      try {
        const fetchedCourse = await getCourseById(courseId);
        
        if (fetchedCourse && fetchedCourse.type === "individual") {
          setCourse(fetchedCourse);
        } else {
          toast({
            title: "Error",
            description: "Course not found or not an individual course.",
            variant: "destructive",
          });
          navigate("/student-dashboard/courses");
          return;
        }
      } catch (error) {
        console.error("Error loading course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [courseId, navigate, toast]);

  const handleEnroll = () => {
    navigate(`/student-dashboard/individual-course/${courseId}/enroll`);
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <p>Loading course details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="student">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-2">Course Not Found</h2>
          <p className="text-gray-500 mb-4">The course you're looking for could not be found.</p>
          <Button onClick={() => navigate("/student-dashboard/courses")}>
            Browse Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/student-dashboard/courses")}
          className="mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Courses
        </Button>
        
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
            <p className="text-gray-600 mt-2">{course.description}</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Course Overview</CardTitle>
              <CardDescription>Key information about this course</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Hourly Rate</h3>
                    <p className="text-sm text-gray-600">£{course.pricePerHour?.toFixed(2)}/hour</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Info className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium">Flexible Learning</h3>
                <p className="text-gray-500 mt-2 max-w-md mx-auto">
                  Our experienced instructors will help you achieve your driving goals at your own pace.
                </p>
                <Button onClick={handleEnroll} className="mt-6">
                  Book Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IndividualCourseView; 